import { Link } from "react-router-dom";
import {
  Search, Users, Zap, Repeat, CalendarClock, Brain, ArrowRight,
} from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub } from "@/components/web/atoms";
import { Reveal, trackHomeEvent, scrollToId } from "@/components/web/home/primitives";

/* Section 2 — "What do you want to accomplish?" Six capability cards, each a
   clear entry point into an existing TeamNest capability. */
const CARDS = [
  {
    icon: Search,
    tone: "ai",
    title: "Research Anything",
    copy: "Ask one AI for speed, or compare multiple models when the answer matters.",
    points: ["Ask one AI", "Compare multiple models", "Synthesize findings", "Save the result"],
    cta: "Start AI Research",
    to: "/ai-research",
    event: "capability-ai-research",
  },
  {
    icon: Users,
    tone: "brand",
    title: "Work With People",
    copy: "Chat, share files and collaborate on projects — with AI research connected but never overwhelming the conversation.",
    points: ["Human · Combined · AI views", "Shared findings", "Files & projects"],
    cta: "Explore Collaboration",
    scroll: "human-ai",
    event: "capability-collaboration",
  },
  {
    icon: Zap,
    tone: "brand",
    title: "Turn Answers Into Action",
    copy: "AI shouldn't stop at an answer. Create tasks, documents, meetings and follow-ups directly from the result.",
    points: ["Create Task", "Share", "Save", "Add to Project", "Automate"],
    cta: "See AI Actions",
    scroll: "action-bar",
    event: "capability-action",
  },
  {
    icon: Repeat,
    tone: "brand",
    title: "Automate Repetitive Work",
    copy: "Describe what you want TeamNest to do in plain English, test it, save it and run it again.",
    points: ['"Every Monday, summarize overdue tasks and post to Operations."'],
    cta: "Explore Automations",
    scroll: "automation",
    event: "capability-automation",
  },
  {
    icon: CalendarClock,
    tone: "brand",
    title: "Meet Smarter",
    copy: "Prepare before the meeting, capture the conversation, and turn decisions into work afterward.",
    points: ["Prepare", "Meet", "Execute"],
    cta: "See Meeting Intelligence",
    scroll: "meeting-intelligence",
    event: "capability-meeting",
  },
  {
    icon: Brain,
    tone: "ai",
    title: "Never Start From Zero",
    copy: "TeamNest remembers your research, documents, decisions and team knowledge so you can continue where you left off.",
    points: ["Personal memory", "Team knowledge", "Decisions & files"],
    cta: "Explore TeamNest Memory",
    scroll: "memory",
    event: "capability-memory",
  },
];

function Card({ c, delay }) {
  const accent = c.tone === "ai" ? "var(--w-ai)" : "var(--w-brand)";
  const tint = c.tone === "ai" ? "var(--w-ai-tint)" : "var(--w-brand-tint)";
  const ctaClassName = "mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold self-start transition-colors";
  const ctaContent = (
    <>
      {c.cta}
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
    </>
  );
  return (
    <Reveal delay={delay}>
      <div
        className="group h-full flex flex-col rounded-[18px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--w-hairline-strong)]"
        style={{ boxShadow: "0 1px 0 rgba(255,255,255,0.02) inset" }}
      >
        <span className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: tint }}>
          <c.icon className="w-5 h-5" style={{ color: accent }} />
        </span>
        <h3 className="text-[19px] font-bold tracking-[-0.01em] text-[var(--w-text)]">{c.title}</h3>
        <p className="mt-2 text-[14px] leading-6 text-[var(--w-text-dim)]">{c.copy}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {c.points.map((p) => (
            <span
              key={p}
              className="inline-flex items-center px-2.5 py-1 rounded-full border border-[var(--w-hairline)] bg-[var(--w-bg2)] text-[11.5px] text-[var(--w-text-dim)]"
            >
              {p}
            </span>
          ))}
        </div>
        {c.to ? (
          <Link
            to={c.to}
            data-testid={c.event}
            onClick={() => trackHomeEvent(c.event, { source: "capability_card" })}
            className={ctaClassName}
            style={{ color: accent }}
          >
            {ctaContent}
          </Link>
        ) : (
          <button
            type="button"
            data-testid={c.event}
            onClick={() => {
              trackHomeEvent(c.event, { source: "capability_card" });
              scrollToId(c.scroll);
            }}
            className={ctaClassName}
            style={{ color: accent }}
          >
            {ctaContent}
          </button>
        )}
      </div>
    </Reveal>
  );
}

export default function CapabilityCards() {
  return (
    <section
      id="accomplish"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-capabilities-accomplish"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">What can you do with TeamNest?</Eyebrow>
          <SectionTitle>What do you want to accomplish?</SectionTitle>
          <SectionSub className="mt-5">
            Start with AI, people, a meeting, a document, a project, or an automation.
          </SectionSub>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CARDS.map((c, i) => (
            <Card key={c.title} c={c} delay={(i % 3) * 80} />
          ))}
        </div>
      </div>
    </section>
  );
}
